import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface PurchasedCredit {
  amount: number;
  remaining: number;
  expiresAt: Date;
  daysUntilExpiry: number;
}

interface DetailedCreditUsage {
  inputCredits: number;
  outputCredits: number;
  totalUsed: number;
  limit: number;
  inputPercentage: number;
  outputPercentage: number;
  monthYear: string;
  renewalDate: Date;
  purchasedCredits: PurchasedCredit[];
}

export const useDetailedCreditsUsage = () => {
  const { user } = useAuth();
  const [data, setData] = useState<DetailedCreditUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetailedUsage = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Buscar uso mensal
        const currentDate = new Date();
        const monthYear = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

        const { data: monthlyData } = await supabase
          .from('user_credits')
          .select('*')
          .eq('user_id', user.id)
          .eq('month_year', monthYear)
          .single();

        // Buscar créditos comprados
        const { data: purchasedData } = await supabase
          .rpc('get_available_purchased_credits', { p_user_id: user.id });

        // Calcular data de renovação (primeiro dia do próximo mês)
        const renewalDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

      // Formatar créditos comprados com validação
      const formattedPurchased = purchasedData?.map((pc: any) => {
        const expiresAt = new Date(pc.expires_at);
        const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return {
          amount: pc.credits_purchased || 0,
          remaining: pc.credits_remaining || 0,
          expiresAt,
          daysUntilExpiry
        };
      }).filter((pc: any) => pc.amount > 0 && pc.remaining > 0) || [];

        if (monthlyData) {
          const inputCredits = monthlyData.credits_input || 0;
          const outputCredits = monthlyData.credits_output || 0;
          const totalUsed = inputCredits + outputCredits;
          const limit = monthlyData.limit_credits || 0;

          setData({
            inputCredits,
            outputCredits,
            totalUsed,
            limit,
            inputPercentage: limit > 0 ? (inputCredits / limit) * 100 : 0,
            outputPercentage: limit > 0 ? (outputCredits / limit) * 100 : 0,
            monthYear,
            renewalDate,
            purchasedCredits: formattedPurchased
          });
        } else {
          // Usuário sem dados ainda - obter limite
          const limits: Record<string, number> = {
            free: 10000,
            basic: 50000,
            pro: 500000,
            premium: 2000000
          };

          const planLimit = limits.free;

          setData({
            inputCredits: 0,
            outputCredits: 0,
            totalUsed: 0,
            limit: planLimit,
            inputPercentage: 0,
            outputPercentage: 0,
            monthYear,
            renewalDate,
            purchasedCredits: formattedPurchased
          });
        }
      } catch (error) {
        console.error('Erro ao buscar detalhes de créditos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetailedUsage();
  }, [user]);

  return { data, isLoading };
};
